import { booleanFilter } from '~/shared/utils';
import { Types } from '~/graphql';
import { ContentTransformers } from '~/shared/transformers';
import { formatEnum } from '~/shared/formatters';
import { initializeApollo } from '~/core/apollo';
import { GetCompanyConfiguration } from './queries';
import { GetCompanyConfigurationQuery } from './queries.generated';
import getContentByExternalId from '~/shared/utils/queries/getContentByExternalId';
import getContentByContentId from '~/shared/utils/queries/getContentByContentId';
import { ParsedUrlQuery } from 'querystring';
import type { IntlShape } from 'react-intl';

/**
 * List of tenant ids that have a custom version of the platform.
 */
export const companyTenantsIds: Array<Types.UUID> = JSON.parse(
    process.env.NEXT_PUBLIC_TENANT_IDS || '[]'
);

export type TenantConfig = GetCompanyConfigurationQuery['company'] & {
    contentTitle?: string;
};

export const getTenantsConfiguration = async (): Promise<Array<TenantConfig>> => {
    try {
        if (!companyTenantsIds || companyTenantsIds.length === 0) {
            return [];
        }

        const apolloClient = initializeApollo();
        const configurations = await Promise.all(
            companyTenantsIds
                .map(async (tenantId) => {
                    try {
                        const { data } = await apolloClient.query({
                            query: GetCompanyConfiguration,
                            variables: {
                                companyId: tenantId
                            }
                        });
                        return data?.company;
                    } catch (error) {
                        console.error(`Error fetching config for tenant ${tenantId}:`, error);
                        return null;
                    }
                })
                .filter(booleanFilter)
        );

        return configurations;
    } catch (error) {
        console.error('Error while fetching tenants configuration:', error);
        return [];
    }
};

const isOnContentPage = (asPath: string) => {
    return (
        !asPath.includes('/mediathek/') &&
        (asPath.match(/\/content\/[^/]+/) || asPath.match(/\/booking\/events\/[^/]+/))
    );
};

const isOnPartnerPage = (pathname: string) => {
    return pathname.includes('/partner-fortbildungsforum/[companyIdOrSlug]');
};

const isOnUserPages = (asPath: string) => {
    return [
        '/user/login',
        '/user/registration',
        '/user/complete-profile',
        '/user/forgot-password',
        '/user/reset-password'
    ].some((path) => asPath.includes(path));
};

export const isPathAffectedByTenantConfig = (asPath: string, pathname: string) => {
    return isOnContentPage(asPath) || isOnPartnerPage(pathname) || isOnUserPages(asPath);
};

type GetTenantConfigFromPathnameProps = {
    tenantsConfig: Array<TenantConfig>;
    asPath: string;
    pathname: string;
    query: ParsedUrlQuery;
    formatMessage: IntlShape['formatMessage'];
};
export const getTenantConfigFromPathname = async ({
    tenantsConfig,
    asPath,
    pathname,
    query,
    formatMessage
}: GetTenantConfigFromPathnameProps): Promise<TenantConfig | null> => {
    if (!tenantsConfig || tenantsConfig.length === 0) {
        return null;
    }

    if (isOnContentPage(asPath) && typeof query?.id === 'string' && query.id != null) {
        const { content } = asPath.match(/\/booking\/events\/[^/]+/)
            ? await getContentByExternalId(query.id)
            : await getContentByContentId(query.id);
        const currentTenantConfig = tenantsConfig.find(
            (config) => content?.provider?.id === config?.id
        );
        if (currentTenantConfig && content) {
            return {
                ...currentTenantConfig,
                contentTitle: `${content?.title || ''} - ${formatEnum(content.brand, {
                    options: ContentTransformers.brand,
                    formatMessage
                })}`
            };
        }
        return null;
    } else if (isOnPartnerPage(pathname)) {
        return (
            tenantsConfig.find((config) => asPath.includes(config?.companyPage?.slug || '')) || null
        );
    } else if (isOnUserPages(asPath)) {
        const tenant = query.tenant as string;
        if (!tenant) {
            return null;
        }
        return tenantsConfig.find((config) => tenant === config.companyPage?.slug) || null;
    }
    return null;
};
